import { MemberRole } from 'src/features/member/types'

import { WorksheetAction, WorksheetStatus } from '../types'

/**
 * ワークシートアクションの権限定義
 */
type WorksheetActionPermission = {
  /** 許可されるロール */
  allowedRoles: MemberRole[]
  /** 許可される試験施設ロール (nullの場合は制限なし) */
  allowedTrialHospitalRoles: ('Main' | 'Partner')[] | null
}

/**
 * 各ワークシートアクションの権限定義（使用するアクションのみ定義）
 */
const WORKSHEET_ACTION_PERMISSIONS: Partial<
  Record<WorksheetAction, WorksheetActionPermission>
> = {
  save: {
    allowedRoles: ['Dr', 'CRC'],
    allowedTrialHospitalRoles: null,
  },
  confirm: {
    allowedRoles: ['Dr'],
    allowedTrialHospitalRoles: null,
  },
  reEdit: {
    allowedRoles: ['Dr', 'CRC'],
    allowedTrialHospitalRoles: null,
  },
  disable: {
    allowedRoles: ['Dr', 'CRC'],
    allowedTrialHospitalRoles: null,
  },
  enable: {
    allowedRoles: ['Dr', 'CRC'],
    allowedTrialHospitalRoles: null,
  },
  deMainSave: {
    allowedRoles: ['Dr', 'CRC'],
    allowedTrialHospitalRoles: ['Main'],
  },
  deSend: {
    allowedRoles: ['Dr'],
    allowedTrialHospitalRoles: ['Main'],
  },
  dePartnerSave: {
    allowedRoles: ['Dr', 'CRC'],
    allowedTrialHospitalRoles: ['Partner'],
  },
  deAccept: {
    allowedRoles: ['Dr', 'CRC'],
    allowedTrialHospitalRoles: ['Partner'],
  },
  deReEdit: {
    allowedRoles: ['Dr', 'CRC'],
    allowedTrialHospitalRoles: ['Main'],
  },
  deWithdraw: {
    allowedRoles: ['Dr', 'CRC'],
    allowedTrialHospitalRoles: ['Partner'],
  },
}

/**
 * 指定されたワークシートアクションを実行する権限があるかチェック
 * @param action ワークシートアクション
 * @param memberRole メンバーロール
 * @param trialHospitalRole 試験施設ロール
 * @returns 権限がある場合はtrue、ない場合はfalse
 */
export const canPerformWorksheetAction = (
  action: WorksheetAction,
  memberRole: MemberRole,
  trialHospitalRole: 'Main' | 'Partner' | null,
): boolean => {
  const permission = WORKSHEET_ACTION_PERMISSIONS[action]

  if (!permission) {
    return false
  }

  // ロールチェック
  if (!permission.allowedRoles.includes(memberRole)) {
    return false
  }

  // 試験施設ロールチェック
  if (permission.allowedTrialHospitalRoles !== null) {
    if (trialHospitalRole === null) {
      return false
    }
    if (!permission.allowedTrialHospitalRoles.includes(trialHospitalRole)) {
      return false
    }
  }

  return true
}

// 各Saveアクションが表示されるワークシートステータス定義
export const saveActionStatusMap: {
  [key in WorksheetAction]?: WorksheetStatus[]
} = {
  save: ['created', 'saved'],
  deMainSave: ['deCreated', 'dePresend'],
  dePartnerSave: ['deAwaitingReceipt'],
}

/**
 * DocumentExchange型のワークシートかどうかを判定
 * DocumentExchange型のステータスは 'de' プレフィックスを持つ
 * @param worksheetStatus ワークシートステータス
 * @returns DocumentExchange型の場合はtrue
 */
const isDocumentExchangeWorksheet = (
  worksheetStatus: WorksheetStatus,
): boolean => {
  return worksheetStatus.startsWith('de')
}

/**
 * ワークシートの印刷が可能かチェック
 * - DocumentExchange型のワークシートのみ許可
 * - Dr・CRCロールのみ許可
 * - 未保存状態（deCreated）は印刷不可
 * @param memberRole メンバーロール
 * @param worksheetStatus ワークシートステータス
 * @returns 印刷可能な場合はtrue、不可の場合はfalse
 */
export const canPrintWorksheet = (
  memberRole: MemberRole,
  worksheetStatus: WorksheetStatus,
): boolean => {
  // DocumentExchange型のワークシートのみ許可
  if (!isDocumentExchangeWorksheet(worksheetStatus)) {
    return false
  }

  // Dr・CRCのみ許可
  const allowedRoles: MemberRole[] = ['Dr', 'CRC']
  if (!allowedRoles.includes(memberRole)) {
    return false
  }

  // 未保存状態（deCreated）は印刷不可
  const unsavedStatuses: WorksheetStatus[] = ['deCreated']
  if (unsavedStatuses.includes(worksheetStatus)) {
    return false
  }

  return true
}
